'use client';

import { register as registerAction } from "@/actions/auth";
import { useGoogleReCaptcha, useServerTranslations } from "@/lib";
import { event } from "@/lib/analytics/gtag";
import { Alert, Card, Header } from '@ui/composites';
import { Button, Input, LocalizedLink } from '@ui/primitives';
import { Section } from '@ui/layouts';
import { SocialConnect } from "@/components/forms/socialConnect";
import classNames from "classnames";
import { useRouter } from "next/navigation";
import { useActionState, useEffect, useTransition } from "react";
import { useForm } from "react-hook-form";

interface RegisterClientProps {
    locale: string;
    dictionary: Record<string, any>;
    recaptchaSiteKey: string;
}

interface RegisterFormData {
    email: string;
}

const translateMessage = (t: Function, message?: string) => {
    if (!message) {
        return "";
    }

    const translated = t(message);
    return typeof translated === "string" && translated !== message ? translated : message;
};

export const RegisterClient = ({ locale, dictionary, recaptchaSiteKey }: RegisterClientProps) => {
    const router = useRouter();
    const { t } = useServerTranslations(dictionary);
    const [isPending, startTransition] = useTransition();
    const [state, formAction] = useActionState(registerAction, undefined);
    const { register, getValues, formState: { errors } } = useForm<RegisterFormData>();
    const { getToken } = useGoogleReCaptcha(recaptchaSiteKey, locale);

    useEffect(() => {
        const targetLocale = locale || 'fr-fr';
        const emailValue = getValues('email');

        if (state?.isError && state.message === 'errors.user_already_exists') {
            if (emailValue) {
                sessionStorage.setItem('registration_email', emailValue);
            }
            router.push(`/${targetLocale}/login`);
            return;
        }

        if (state?.isError === false) {
            try {
                event({
                    action: 'register_request',
                    category: 'auth',
                    label: 'passwordless'
                });
            } catch (e) {
                console.error('Analytics event failed:', e);
            }
            if (emailValue) {
                sessionStorage.setItem('registration_email', emailValue);
            }
            router.push(`/${targetLocale}/registration-pending`);
        }
    }, [state, router, locale, getValues]);

    return (
        <Section container className="py-8">
            <div className="max-w-md mx-auto w-full">
                <div className="space-y-6 sm:space-y-8">
                    <Header
                        title={t('auth.register_title')}
                        description={t('auth.register_subtitle')}
                        align="center"
                        size="xl"
                    />

                    <Card className="p-6">
                        <form
                            action={async (formData) => {
                                formData.set('recaptchaToken', await getToken('register'));
                                startTransition(() => formAction(formData));
                            }}
                            className="space-y-4 sm:space-y-6"
                            noValidate
                        >
                            <div role="status" aria-live="polite" aria-atomic="true">
                                {state?.isError === false && (
                                    <Alert icon="success" className="mb-4">
                                        {t('auth.register_confirmation_sent')}
                                    </Alert>
                                )}
                                {state?.isError && (
                                    <Alert icon="error" className="mb-4">
                                        {translateMessage(t, state.message)}
                                    </Alert>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label
                                    htmlFor="email"
                                    className={classNames(
                                        "block text-sm font-medium",
                                        { 'text-error-700': errors.email },
                                        { 'text-gray-700': !errors.email }
                                    )}
                                >
                                    {t('forms.email_label')}
                                    <span className="text-error-500 ml-1">*</span>
                                </label>
                                <Input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    placeholder={t('forms.email_placeholder')}
                                    {...register('email', {
                                        required: t('forms.email_errors_required'),
                                        pattern: {
                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                            message: t('forms.email_errors_invalid')
                                        }
                                    })}
                                    name="email"
                                    error={errors.email?.message}
                                    className="w-full text-base sm:text-sm"
                                />
                            </div>
                            <Button
                                type="submit"
                                variant="secondary"
                                disabled={isPending}
                                loading={isPending}
                                icon={<i className="fas fa-envelope mr-2" />}
                                className="w-full">{t('auth.links_send_code')}</Button>
                        </form>

                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <p className="text-sm text-gray-500 text-center mb-4">{t('auth.use_services')}</p>
                            <SocialConnect locale={locale} />
                        </div>
                    </Card>

                    <nav className="text-center">
                        <p className="text-sm text-gray-500 mb-4">
                            {t('auth.register_already_have_account')}
                        </p>
                        <LocalizedLink
                            variant="primary"
                            locale={locale}
                            href="/login"
                        >
                            {t('auth.links_login')}
                        </LocalizedLink>
                    </nav>
                </div>
            </div>
        </Section>
    );
};
